/**
 * Data Formatters & Helpers
 * Utility functions untuk formatting data
 */

import { STOCK_UI } from '@diamond/shared/decimal-util-web';
import {
  buyerCurrencyFormatter,
  numberWithSeparator,
} from '@diamond/shared/utils';
import { dinero, toDecimal } from 'dinero.js';

import type { EcommerceItem } from '../tracking/build-ecommerce-items';
import type { Product } from './index';

// ============================================================================
// Currency Formatters
// ============================================================================

/**
 * Format number ke currency Rupiah
 * @example formatCurrency(1000000) => "Rp1.000.000"
 */
export const formatCurrency = (amount: number): string => {
  return buyerCurrencyFormatter(amount);
};

/**
 * Format number dengan thousand separator
 * @example formatNumber(1000000) => "1.000.000"
 */
export const formatNumber = (value: number): string => {
  return numberWithSeparator(value);
};

// ============================================================================
// Stock Formatters
// ============================================================================

/**
 * Convert stock dari smallest unit ke display unit
 * @example formatStock(1000) => 1 (jika 1000 = 1 unit)
 */
export const formatStock = (stock: number): number => {
  return Number(
    toDecimal(
      dinero({
        amount: Number(stock),
        currency: STOCK_UI,
      })
    )
  );
};

/**
 * Format stock dengan thousand separator
 */
export const formatStockDisplay = (stock: number): string => {
  const formattedStock = formatStock(stock);
  return formatNumber(formattedStock);
};

// ============================================================================
// Product Formatters
// ============================================================================

/**
 * Format product quantity untuk display
 * Default quantity dalam smallest unit (misal: 1000 = 1 unit)
 */
export const formatProductQuantity = (quantity: number): string => {
  const displayQty = quantity / 1000; // Convert dari smallest unit
  return formatNumber(displayQty);
};

/**
 * Calculate total price untuk single product
 */
export const calculateProductTotal = (
  price: number,
  quantity: number
): number => {
  const displayQty = quantity / 1000;
  return price * displayQty;
};

/**
 * Format products untuk tracking events (e-commerce standard)
 */
export const formatProductsForTracking = (
  products: Product[],
  warehouse: string
): EcommerceItem[] => {
  return products.map((product, index) => ({
    id: product.id,
    item_id: product.item_code?.toString(),
    item_name: product.title,
    item_brand: product.brand_name,
    coupon: product.discount_promo_code || '',
    discount: product.discount_amount || 0,
    index: index,
    item_category: product.category_name || '',
    item_category2: product.sub_category_name || '',
    item_variant: product.type || '',
    location_id: warehouse,
    price: product.price || 0,
    quantity: product.quantity / 1000 || 1,
  }));
};

// ============================================================================
// Phone Number Formatters
// ============================================================================

/**
 * Format nomor telepon untuk display
 * @example formatPhoneNumber("+6281234567890") => "+62 812-3456-7890"
 */
export const formatPhoneNumber = (phone: string): string => {
  if (!phone) return '';

  // Remove all non-numeric characters except +
  const cleaned = phone.replace(/[^\d+]/g, '');

  // Format berdasarkan pola Indonesia
  if (cleaned.startsWith('+62')) {
    const number = cleaned.slice(3);
    if (number.length >= 9) {
      return `+62 ${number.slice(0, 3)}-${number.slice(3, 7)}-${number.slice(
        7
      )}`;
    }
  }

  return cleaned;
};

/**
 * Validate phone number format
 */
export const isValidPhoneNumber = (phone: string): boolean => {
  const cleaned = phone.replace(/[^\d+]/g, '');

  // Indonesia phone number: +62xxxxxxxxxx (min 10 digits after +62)
  const phoneRegex = /^\+62\d{9,13}$/;
  return phoneRegex.test(cleaned);
};

// ============================================================================
// Date Formatters
// ============================================================================

/**
 * Format date untuk display
 * @example formatDate("2024-01-15") => "15 Januari 2024"
 */
export const formatDate = (dateString: string): string => {
  if (!dateString) return '';

  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  };

  return date.toLocaleDateString('id-ID', options);
};

/**
 * Format date untuk API (ISO format)
 */
export const formatDateForApi = (date: Date): string => {
  return date.toISOString().split('T')[0];
};

// ============================================================================
// Address Formatters
// ============================================================================

/**
 * Format full address dari components
 */
export const formatFullAddress = (addressComponents: {
  line1?: string;
  line2?: string;
  city?: string;
  province?: string;
  postalCode?: string;
}): string => {
  const { line1, line2, city, province, postalCode } = addressComponents;

  const parts = [line1, line2, city, province, postalCode].filter(Boolean);
  return parts.join(', ');
};

// ============================================================================
// File Formatters
// ============================================================================

/**
 * Format file size untuk display
 * @example formatFileSize(1024000) => "1 MB"
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * Get file extension dari filename
 */
export const getFileExtension = (filename: string): string => {
  return filename
    .slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2)
    .toLowerCase();
};

/**
 * Validate file type
 */
export const isValidFileType = (
  file: File,
  allowedTypes: string[]
): boolean => {
  const extension = getFileExtension(file.name);
  return allowedTypes.includes(extension);
};

/**
 * Validate file size (in bytes)
 */
export const isValidFileSize = (file: File, maxSizeInMB: number): boolean => {
  const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
  return file.size <= maxSizeInBytes;
};

// ============================================================================
// String Helpers
// ============================================================================

/**
 * Truncate text dengan ellipsis
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
};

/**
 * Capitalize first letter
 */
export const capitalizeFirst = (text: string): string => {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};
